import type { City, Place } from '../world/city';
import type { PhysicsCity } from '../physics/world';
import type { Person, Population } from './population';

interface Operator {
  person: Person;
  building: number;
  places: Place[];
  phase: number;
  task: string;
  sequence: number;
  retry: number;
  checks: number;
  reason: string;
}
/** Fictional city facility: original operators use live rooms; loss of occupied
 * structure or a local fire triggers ordinary shutdown and emergency access. */
export class PowerPlant {
  units: Operator[] = [];
  state: 'starting' | 'operational' | 'damaged' | 'offline' = 'starting';
  reason = 'Operators checking the plant';
  time = 0;
  alarmUntil = 0;
  private epoch = 0;
  private nextCheck = 0;
  private baseline = new Map<number, number>();
  private shutdown = false;
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    this.reset();
  }
  clear(): void {
    for (const u of this.units) this.population.civil?.cancelTask(u.person, 'Plant lifecycle cleared');
    this.units = [];
    this.time = this.nextCheck = this.alarmUntil = 0;
    this.baseline.clear();
    this.shutdown = false;
  }
  reset(): void {
    this.clear();
    this.epoch = this.physics.life.epoch;
    this.state = 'starting';
    this.reason = 'Operators checking the plant';
    const plant = this.city.strategic?.plant,
      civil = this.population.civil;
    if (!plant || !civil) return;
    for (const id of [plant.control, ...plant.halls, ...plant.cooling])
      this.baseline.set(id, this.city.buildings.find((b) => b.id === id)!.cells.length);
    for (const [i, duty] of (plant.duties ?? []).entries()) {
      const slot = duty.places[0];
      if (!civil.network.available(slot)) continue;
      const p = this.population.createCrew(`crew:power:${i}`, slot);
      const node = civil.network.nodes[civil.network.places.get(slot.id)!];
      civil.resume(p, node.layer);
      p.controlled = true;
      p.name = 'East Cape plant operator';
      p.color = 0xdbaf56;
      this.units.push({
        person: p,
        building: duty.building,
        places: duty.places,
        phase: 0,
        task: '',
        sequence: 0,
        retry: i * 0.3,
        checks: 0,
        reason: 'Beginning the assigned inspection',
      });
    }
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch) return;
    this.time += dt;
    const civil = this.population.civil;
    if (!civil) return;
    if (this.time >= this.nextCheck) {
      this.nextCheck = this.time + 1;
      let integrity = 1;
      for (const [id, total] of this.baseline) {
        let anchored = 0;
        for (const c of this.physics.buildingCells(id)) {
          const owner = this.physics.cellOwner.get(c.id),
            cluster =
              owner === undefined
                ? undefined
                : (this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner));
          if (cluster?.anchored) anchored++;
        }
        integrity = Math.min(integrity, anchored / Math.max(1, total));
      }
      const p = this.city.strategic!.parcels.find((p) => p.id === 'power')!;
      const fire = this.population.fire
        .incidents()
        .some(
          (f) =>
            f.point.x > p.minX &&
            f.point.x < p.maxX &&
            f.point.z > p.minZ &&
            f.point.z < p.maxZ &&
            f.heat > 0.7,
        );
      if (!this.shutdown && (integrity < 0.55 || fire)) {
        this.shutdown = true;
        this.alarmUntil = this.time + 12;
        this.population.services?.reportCollapse(p.gate, 18);
      }
      const control = this.city.strategic!.plant.control;
      const staffed = this.units.some(
        (u) =>
          u.building === control &&
          u.checks > 0 &&
          !['incapacitated', 'falling', 'flee'].includes(u.person.state),
      );
      this.state = this.shutdown
        ? 'offline'
        : integrity < 0.96
          ? 'damaged'
          : staffed
            ? 'operational'
            : 'starting';
      this.reason = this.shutdown
        ? 'Plant shut down; transport and emergency services use backup power'
        : this.state === 'damaged'
          ? 'Damage under inspection; backup services available'
          : staffed
            ? 'Operators on duty; generation online'
            : 'Operators completing start-of-shift checks';
    }
    for (const u of this.units) {
      u.retry -= dt;
      if (['incapacitated', 'falling', 'flee'].includes(u.person.state)) continue;
      if (!u.places.every((p) => civil.network.available(p))) {
        civil.cancelTask(u.person, 'Plant workplace is damaged');
        u.task = '';
        u.reason = 'Evacuating the unsafe workplace';
        this.population.evacuate(u.person, u.places[u.phase]);
        continue;
      }
      const task = civil.task(u.person);
      if (u.task && task?.token === u.task && task.state === 'active') {
        u.reason = task.reason;
        continue;
      }
      if (u.task && task?.token === u.task && task.state === 'completed') {
        u.checks++;
        u.phase = (u.phase + 1) % u.places.length;
        u.task = '';
        u.retry = 0.25;
      } else if (u.task) {
        u.task = '';
        u.retry = 3;
      }
      if (u.retry > 0) continue;
      const target = u.places[u.phase],
        token = `power:${u.person.id}:${++u.sequence}`;
      if (
        civil.assignTask(u.person, target, token, this.shutdown ? 'inspecting' : 'working', 12 + u.phase * 6)
      ) {
        u.task = token;
        u.reason = this.shutdown
          ? 'Inspecting the shutdown plant'
          : 'Walking to the assigned control or inspection position';
      } else {
        u.retry = 3;
        u.reason = 'Waiting for safe access to the assigned workplace';
      }
    }
  }
  snapshot(): Record<string, number | string> {
    return {
      plantState: this.state,
      plantReason: this.reason,
      plantOperators: this.units.length,
      plantChecks: this.units.reduce((n, u) => n + u.checks, 0),
    };
  }
  inspect(): unknown {
    return {
      ...this.snapshot(),
      units: this.units.map((u) => ({
        id: u.person.id,
        p: u.person.p.toArray(),
        state: u.person.state,
        checks: u.checks,
        target: u.places[u.phase].id,
        reason: u.reason,
      })),
    };
  }
}
